/**
 * Page d'accueil : le verdict, puis le détail.
 *
 * L'ordre est délibéré. Le verdict, sa raison et les trois faits tiennent dans
 * le premier écran d'un téléphone : la réponse à la question posée est donc
 * COMPLÈTE sans défiler.
 *
 * Vient ensuite le détail, du plus urgent au moins urgent : quels feux, puis
 * ce qu'il y a dans l'air, puis le journal des odeurs. Ce dernier occupait
 * auparavant le milieu de la page alors qu'il ne sert qu'occasionnellement.
 *
 * Un faux titre "Plus de détails" séparait les deux moitiés. Il s'affichait
 * même quand rien de notable ne suivait, et c'était un paragraphe déguisé en
 * titre. Les vrais titres de section font le travail.
 */
import { selectDisplayedVerdict, selectNearbyFires } from '../../state/selectors';
import type { Actions } from '../../state/actions';
import type { AppState } from '../../state/store';
import { airList } from '../components/air-list';
import { fireList } from '../components/fire-list';
import { noseJournal } from '../components/nose-journal';
import { plumeNotice } from '../components/plume-notice';
import { verdictCard } from '../components/verdict-card';
import { html, render } from '../html';

export interface HomePage {
  render(state: AppState): void;
}

export function createHomePage(
  root: HTMLElement,
  deps: { actions: Actions; onZoneSelected: (zoneId: string) => void },
): HomePage {
  // Un seul écouteur, posé une fois, plutôt que des attributs onclick
  // reconstruits à chaque rendu.
  root.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;

    const zoneButton = target.closest<HTMLElement>('[data-zone-id]');
    if (zoneButton?.dataset['zoneId']) {
      deps.onZoneSelected(zoneButton.dataset['zoneId']);
      return;
    }

    const noseButton = target.closest<HTMLElement>('[data-nose]');
    if (noseButton) {
      deps.actions.addNoseEntry(noseButton.dataset['nose'] === 'smoke');
    }
  });

  return {
    render(state) {
      const now = Date.now();
      const fires = selectNearbyFires(state, now);
      const verdict = selectDisplayedVerdict(state, fires, now);

      render(
        root,
        html`
          <div aria-live="polite">
            ${verdictCard(state, fires, verdict, now)}
            ${plumeNotice(state, fires, verdict, now)}
          </div>

          ${fireList(fires, now)} ${airList(state, now)} ${noseJournal(state, now)}

          <footer class="foot">
            <p>
              Les satellites de la NASA passent quelques fois par jour. Un feu récent
              peut ne pas encore apparaître, et une zone peut rester affichée après
              extinction.
            </p>
            <p>
              Application non officielle. En cas de danger, appelle le
              <strong>112</strong>. <a href="#/sources">D'où viennent ces informations</a>
            </p>
          </footer>
        `,
      );
    },
  };
}
